import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  AlertTriangle, MessageSquare, FileText, Clock, 
  CheckCircle, XCircle, Upload, ChevronLeft,
  Shield, User, Package, CreditCard
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  getDisputeDetails, 
  getDisputeEvidence,
  uploadDisputeEvidence,
  addDisputeMessage,
  getDisputeMessages
} from '../../services/api';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';

const DisputeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Evidence state
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newEvidence, setNewEvidence] = useState({
    description: '',
    file: null,
    evidence_type: 'image'
  });
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  useEffect(() => {
    loadDisputeData();
  }, [id]);

  const loadDisputeData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load dispute details
      const disputeResponse = await getDisputeDetails(id);
      if (!disputeResponse.success) {
        throw new Error(disputeResponse.error || 'Failed to load dispute');
      }
      
      setDispute(disputeResponse.data);
      
      // Load evidence
      const evidenceResponse = await getDisputeEvidence(id);
      if (evidenceResponse.success) {
        setEvidence(evidenceResponse.data || []);
      }
      
      // Load messages if available
      try {
        const messagesResponse = await getDisputeMessages(id);
        if (messagesResponse.success) {
          setMessages(messagesResponse.data || []);
        }
      } catch (msgError) {
        // console.log('Messages not available:', msgError);
      }
      
    } catch (err) {
      // console.error('Error loading dispute:', err);
      setError(err.message || 'Failed to load dispute details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Go back to chat if came from there
    if (location.state?.fromChat) {
      navigate(`/chats/${location.state.conversationId}`, {
        state: location.state.chatState
      });
    } else {
      navigate('/support');
    }
  };

  const handleUploadEvidence = async () => {
    if (!newEvidence.file) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', newEvidence.file);
      formData.append('dispute_id', id);
      formData.append('uploaded_by', user.user_id);
      formData.append('evidence_type', newEvidence.evidence_type);
      formData.append('description', newEvidence.description);
      
      const response = await uploadDisputeEvidence(formData);
      
      if (response.success) {
        alert('✅ Evidence uploaded successfully!');
        setShowUploadModal(false);
        setNewEvidence({ description: '', file: null, evidence_type: 'image' });
        await loadDisputeData(); // Refresh evidence list
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      // console.error('Upload error:', error);
      alert('❌ Failed to upload evidence: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sendingMessage) return;
    
    try {
      setSendingMessage(true);
      
      const response = await addDisputeMessage(id, {
        message: newMessage.trim(),
        sender_id: user.user_id
      });
      
      if (response.success) {
        setMessages(prev => [...prev, response.data]);
        setNewMessage('');
      } else {
        throw new Error(response.error || 'Failed to send message');
      }
    } catch (error) {
      // console.error('Send message error:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-4 h-4" /> },
      'in_review': { color: 'bg-blue-100 text-blue-800', icon: <AlertTriangle className="w-4 h-4" /> },
      'resolved': { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
      'closed': { color: 'bg-gray-100 text-gray-800', icon: <XCircle className="w-4 h-4" /> }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon}
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading dispute details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Dispute</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!dispute) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Dispute Not Found</h3>
          <button
            onClick={handleBack}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Determine user's role in dispute
  const isRaisedByUser = user?.user_id === dispute.raised_by_user_id;
  const isAgainstUser = user?.user_id === dispute.raised_against_user_id;
  const isInvolved = isRaisedByUser || isAgainstUser;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Dispute #{dispute.dispute_id}</h1>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusBadge(dispute.status)}
                  <span className="text-sm text-gray-500">
                    Created {formatDate(dispute.created_at)}
                  </span>
                </div>
              </div>
            </div>
            
            {isInvolved && dispute.status === 'pending' && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Add Evidence
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Dispute Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dispute Information Card */}
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Dispute Information
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Amount in Dispute
                    </label>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPrice(dispute.amount)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Category
                    </label>
                    <p className="text-gray-900 font-medium capitalize">
                      {dispute.category?.replace(/_/g, ' ')}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Raised By
                    </label>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {dispute.raised_by_first_name} {dispute.raised_by_last_name}
                      </span>
                      {isRaisedByUser && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          You
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Against
                    </label>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {dispute.raised_against_first_name} {dispute.raised_against_last_name}
                      </span>
                      {isAgainstUser && (
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                          You
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Description
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {dispute.description}
                    </p>
                  </div>
                </div>
                
                {/* Related Information */}
                {dispute.item_title && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Related Item</h3>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Package className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{dispute.item_title}</p>
                        {dispute.dispute_type === 'order' && (
                          <p className="text-sm text-gray-500">Order #{dispute.order_id}</p>
                        )}
                        {dispute.dispute_type === 'escrow' && (
                          <p className="text-sm text-gray-500">Escrow #{dispute.escrow_id}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Evidence Section */}
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Evidence ({evidence.length})
                </h2>
                
                {evidence.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No evidence uploaded yet</p>
                    {isInvolved && dispute.status === 'pending' && (
                      <button
                        onClick={() => setShowUploadModal(true)}
                        className="mt-4 text-orange-600 hover:text-orange-700 font-medium"
                      >
                        + Upload evidence
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {evidence.map((item) => (
                      <div key={item.evidence_id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded capitalize">
                              {item.evidence_type}
                            </span>
                            <p className="text-sm text-gray-500 mt-1">
                              By {item.uploaded_by_first_name} {item.uploaded_by_last_name}
                            </p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {formatDate(item.created_at)}
                          </span>
                        </div>
                        
                        {item.description && (
                          <p className="text-gray-700 text-sm mb-3">{item.description}</p>
                        )}
                        
                        {item.file_url && (
                          <a
                            href={item.full_file_url || item.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FileText className="w-4 h-4" />
                            View {item.file_name || 'File'}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Messages Section (if available) */}
            {messages.length > 0 && (
              <div className="bg-white rounded-xl border shadow-sm">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-500" />
                    Messages ({messages.length})
                  </h2>
                  
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div key={message.message_id} className={`p-3 rounded-lg ${
                        message.sender_id === user?.user_id 
                          ? 'bg-blue-50 border border-blue-100' 
                          : 'bg-gray-50'
                      }`}>
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-gray-900">
                            {message.sender_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-700">{message.message}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Message Input */}
                  <div className="mt-4 border-t pt-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sendingMessage}
                        className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50"
                      >
                        {sendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Status & Actions */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Status Timeline</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Dispute Created</p>
                      <p className="text-sm text-gray-500">
                        {formatDate(dispute.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  {dispute.status === 'in_review' && (
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Under Review</p>
                        <p className="text-sm text-gray-500">
                          Support team is reviewing the evidence
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {dispute.resolution && (
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 capitalize">
                          {dispute.resolution.replace('_', ' ')}
                        </p>
                        {dispute.resolution_notes && (
                          <p className="text-sm text-gray-500 mt-1">
                            {dispute.resolution_notes}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Support Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                Support Information
              </h3>
              
              {dispute.ticket_id ? (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-3">
                    This dispute has been assigned a support ticket for further assistance.
                  </p>
                  <button
                    onClick={() => navigate(`/support/tickets/${dispute.ticket_id}`)}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    View Support Ticket
                  </button>
                </div>
              ) : (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-3">
                    Need additional help? Contact our support team.
                  </p>
                  <button
                    onClick={() => navigate('/support/new-ticket', {
                      state: { dispute_id: dispute.dispute_id }
                    })}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Contact Support
                  </button>
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-xs text-gray-600">
                  <strong>Note:</strong> Funds are frozen until this dispute is resolved.
                  Our support team typically responds within 24-48 hours.
                </p>
              </div>
            </div>

            {/* Escrow Info (if applicable) */}
            {dispute.dispute_type === 'escrow' && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-orange-500" />
                  Escrow Information
                </h3>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>Status:</strong> Funds frozen in escrow
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Amount:</strong> {formatPrice(dispute.amount)}
                </p>
                <button
                  onClick={() => navigate(`/chats/${dispute.conversation_id}`)}
                  className="w-full mt-4 bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700 text-sm"
                >
                  Go to Conversation
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Evidence Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Upload Evidence"
        size="md"
        showCloseButton={true}
        showFooter={true}
        confirmText={uploading ? "Uploading..." : "Upload Evidence"}
        cancelText="Cancel"
        onConfirm={handleUploadEvidence}
        onCancel={() => setShowUploadModal(false)}
        confirmButtonType="primary"
        confirmDisabled={uploading || !newEvidence.file}
      >
        <div className="py-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Evidence Type
            </label>
            <select
              value={newEvidence.evidence_type}
              onChange={(e) => setNewEvidence(prev => ({ ...prev, evidence_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="image">Image (JPG, PNG, GIF)</option>
              <option value="document">Document (PDF, DOC)</option>
              <option value="message_screenshot">Message Screenshot</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={newEvidence.description}
              onChange={(e) => setNewEvidence(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this evidence shows..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              rows="3"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              {newEvidence.file ? (
                <div>
                  <p className="text-sm text-gray-700">{newEvidence.file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(newEvidence.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    type="button"
                    onClick={() => setNewEvidence(prev => ({ ...prev, file: null }))}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag & drop or click to select file
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    Max 10MB. Supported: JPG, PNG, PDF, DOC
                  </p>
                  <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm font-medium text-gray-700">
                    Choose File
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setNewEvidence(prev => ({ ...prev, file }));
                        }
                      }}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              <strong>Tip:</strong> Upload clear evidence like photos of damaged items, 
              screenshots of conversations, or delivery receipts.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DisputeDetail;